import { LitElement, css, html } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';

import { authenticatedAPICall } from '../../client/api-client';
import '../../elements/langhout-button/langhout-button';
import { confirmPopup, showNotification } from '../../LanghoutBetonApp';
import {
  AutomaticStockCount,
  AutomaticStockCountStatus,
  ProductionUnit,
} from '../../types/types';
import {
  automaticStockCountURL,
  invalidateAutomaticStockCount,
  productionUnitNames,
} from '../../util/automatic-stock-count';
import { formatDate, formatTimeWithoutDate } from '../../util/util';
import { linkIcon } from '../../elements/icons/icons';

const confirmInvalidAutomaticStockCountEvent =
  'confirm_invalid_automatic_stock_count';

@customElement('langhout-beton-automatic-stock-counts')
export class LanghoutBetonAutomaticStockCounts extends LitElement {
  static styles = css`
    :host([disableButtons]) {
      pointer-events: none;
      opacity: 0.7;
    }

    .card {
      border: 1px solid var(--text-color-main);
      padding: 12px;
      margin-bottom: 20px;
      border-radius: 10px;
      position: relative;
    }

    .order-icon {
      position: absolute;
      top: 10px;
      right: 10px;
    }

    .order-icon svg {
      width: 30px;
      height: 30px;
    }

    .row {
      display: flex;
      flex-flow: row wrap;
      width: 100%;
    }

    .label {
      color: var(--primary-color);
      min-width: 130px;
      text-align: left;
    }

    .normal-text {
      max-width: 200px;
      overflow-wrap: break-word;
      padding-right: 15px;
      text-align: left;
    }

    .controls {
      display: flex;
      gap: 10px;
      justify-content: space-between;
      margin-top: 20px;
    }

    .button-container {
      max-width: 200px;
      width: 100%;
    }

    .button-container langhout-button {
      display: block;
    }

    @media (max-width: 475px) {
      .controls {
        flex-wrap: wrap;
      }
    }
  `;

  @property({ type: Boolean, reflect: true })
  private disableButtons = false;

  @property({ type: Array })
  private automaticStockCounts: AutomaticStockCount[] = [];

  private productionUnitNames = new Map<string, string>();

  private automaticStockCountToInvalidate: string | null = null;

  constructor() {
    super();
    this.loadTasks();
    document.addEventListener(
      'refresh-tasks',
      this.refreshTaskListener as EventListener,
    );
    document.addEventListener(
      confirmInvalidAutomaticStockCountEvent,
      this.confirmInvalidListener as EventListener,
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(
      'refresh-tasks',
      this.refreshTaskListener as EventListener,
    );
    document.removeEventListener(
      confirmInvalidAutomaticStockCountEvent,
      this.confirmInvalidListener as EventListener,
    );
  }

  private refreshTaskListener = () => {
    this.loadTasks();
  };

  private confirmInvalidListener = async () => {
    const automaticStockCountID = this.automaticStockCountToInvalidate;
    if (!automaticStockCountID || this.disableButtons) {
      return;
    }

    this.disableButtons = true;
    try {
      if (
        await invalidateAutomaticStockCount(
          automaticStockCountID,
          authenticatedAPICall,
        )
      ) {
        await this.loadTasks();
      } else {
        showNotification('De ongeregistreerde telling kon niet worden afgekeurd');
      }
    } finally {
      this.automaticStockCountToInvalidate = null;
      this.disableButtons = false;
    }
  };

  render() {
    if (this.automaticStockCounts.length === 0) {
      return html``;
    }

    return html`
      <h2>Ongeregistreerde tellingen</h2>
      ${repeat(
        this.automaticStockCounts,
        automaticStockCount => automaticStockCount.id,
        automaticStockCount => html`
          <div class="card">
            <div class="order-icon">${linkIcon()}</div>
            <div class="row">
              <div class="label">Machine:</div>
              <div class="normal-text">
                ${
                  this.productionUnitNames.get(
                    automaticStockCount.production_unit_id,
                  ) ?? 'Onbekende productie-eenheid'
                }
              </div>
            </div>
            <div class="row">
              <div class="label">Datum:</div>
              <div class="normal-text">
                ${formatDate(automaticStockCount.time)}
              </div>
            </div>
            <div class="row">
              <div class="label">Tijd:</div>
              <div class="normal-text">
                ${formatTimeWithoutDate(automaticStockCount.time)}
              </div>
            </div>
            <div class="controls">
              <div class="button-container">
                <langhout-button
                  removeButton
                  text="Ongeldig"
                  @click=${() => this.confirmInvalid(automaticStockCount.id)}
                ></langhout-button>
              </div>
              <div class="button-container">
                <langhout-button
                  text="Telling invoeren"
                  @click=${() => this.startStockCount(automaticStockCount)}
                ></langhout-button>
              </div>
            </div>
          </div>
        `,
      )}
    `;
  }

  private confirmInvalid(automaticStockCountID: string) {
    this.automaticStockCountToInvalidate = automaticStockCountID;
    confirmPopup({
      confirm_event: confirmInvalidAutomaticStockCountEvent,
      title: 'Ongeregistreerde telling ongeldig verklaren',
      description: 'Weet u het zeker?',
    });
  }

  private startStockCount(automaticStockCount: AutomaticStockCount) {
    window.location.href = automaticStockCountURL(automaticStockCount);
  }

  private loadTasks = async () => {
    const [automaticStockCountResult, productionUnitResult] = await Promise.all(
      [
        authenticatedAPICall(
          `/stock/automatic-count?status=${AutomaticStockCountStatus.Unvalidated}`,
          'GET',
          new Headers(),
        ),
        authenticatedAPICall('/production-unit', 'GET', new Headers()),
      ],
    );

    if (productionUnitResult.statusCode === 200) {
      const productionUnits = productionUnitResult.body as ProductionUnit[];
      this.productionUnitNames = productionUnitNames(productionUnits);
    }

    if (automaticStockCountResult.statusCode === 200) {
      this.automaticStockCounts =
        automaticStockCountResult.body as AutomaticStockCount[];
    }
  };
}

declare global {
  interface HTMLElementTagNameMap {
    'langhout-beton-automatic-stock-counts': LanghoutBetonAutomaticStockCounts;
  }
}
